import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { youtubeAdminAPI, artistsAPI } from '../services/api';
import SearchBar from '../components/YouTubeImport/SearchBar';
import ImportSummary from '../components/YouTubeImport/ImportSummary';
import VideoTable from '../components/YouTubeImport/VideoTable';
import Pagination from '../components/YouTubeImport/Pagination';
import { parseBattleTitle, findBestArtistMatch } from '../utils/battleTitleParser';
import AlertModal from '../components/common/AlertModal';

const YouTubeImport = () => {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [videoForms, setVideoForms] = useState({});
  const [importing, setImporting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parseProgress, setParseProgress] = useState({ current: 0, total: 0 });
  const [nextPageToken, setNextPageToken] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [artists, setArtists] = useState([]);
  const [pageTokenHistory, setPageTokenHistory] = useState([]);
  const [alertModal, setAlertModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    if (user && isAdmin()) {
      fetchVideos();
      fetchArtists();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchArtists = async () => {
    try {
      const response = await artistsAPI.getAll();
      setArtists(response.data);
    } catch (error) {
      console.error('Error fetching artists:', error);
    }
  };

  const MAX_FETCH_PAGES = 5; // Prevent infinite recursion when most videos are already imported

  const fetchVideos = async (pageToken = null, search = '', accumulatedVideos = [], depth = 0) => {
    if (depth === 0) {
      setLoading(true);
    }
    try {
      const params = {
        per_page: 50, // Fetch max to reduce API calls
        page_token: pageToken,
        search: search || searchQuery,
      };

      const response = await youtubeAdminAPI.getVideos(params);
      const newVideos = response.data.videos || [];
      const allVideos = [...accumulatedVideos, ...newVideos];

      // If we have enough videos, no more pages, or hit max depth, stop
      if (allVideos.length >= perPage || !response.data.next_page_token || depth >= MAX_FETCH_PAGES) {
        setVideos(allVideos.slice(0, perPage)); // Show only perPage amount
        setNextPageToken(response.data.next_page_token);
        setLoading(false);
        return;
      }

      // Auto-fetch next page if we don't have enough results
      await fetchVideos(response.data.next_page_token, search, allVideos, depth + 1);
    } catch (error) {
      console.error('Error fetching YouTube videos:', error);
      setAlertModal({
        show: true,
        title: 'Error',
        message: 'Failed to fetch videos from YouTube',
        type: 'error',
      });
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setNextPageToken(null);
    setPageTokenHistory([]);
    fetchVideos(null, searchQuery);
  };

  const handleSelectVideo = (video) => {
    const newSelected = new Set(selectedVideos);
    if (newSelected.has(video.youtube_id)) {
      newSelected.delete(video.youtube_id);
      const newForms = { ...videoForms };
      delete newForms[video.youtube_id];
      setVideoForms(newForms);
    } else {
      newSelected.add(video.youtube_id);
      // Initialize form for this video
      setVideoForms({
        ...videoForms,
        [video.youtube_id]: {
          rappers: [
            { name: '', existing_id: null },
            { name: '', existing_id: null },
          ],
          event_name: '',
        },
      });
    }
    setSelectedVideos(newSelected);
  };

  const handleRapperChange = (youtubeId, index, value) => {
    setVideoForms({
      ...videoForms,
      [youtubeId]: {
        ...videoForms[youtubeId],
        rappers: videoForms[youtubeId].rappers.map((r, i) =>
          i === index ? { ...r, name: value } : r
        ),
      },
    });
  };

  const handleRapperSelect = (youtubeId, index, existingId, displayName) => {
    setVideoForms({
      ...videoForms,
      [youtubeId]: {
        ...videoForms[youtubeId],
        rappers: videoForms[youtubeId].rappers.map((r, i) =>
          i === index ? { name: displayName, existing_id: existingId } : r
        ),
      },
    });
  };

  const handleEventChange = (youtubeId, value) => {
    setVideoForms({
      ...videoForms,
      [youtubeId]: {
        ...videoForms[youtubeId],
        event_name: value,
      },
    });
  };

  const handleAddRapper = (youtubeId) => {
    setVideoForms({
      ...videoForms,
      [youtubeId]: {
        ...videoForms[youtubeId],
        rappers: [
          ...videoForms[youtubeId].rappers,
          { name: '', existing_id: null },
        ],
      },
    });
  };

  const handleRemoveRapper = (youtubeId, index) => {
    if (videoForms[youtubeId].rappers.length <= 2) {
      setAlertModal({
        show: true,
        title: 'Cannot Remove',
        message: 'At least 2 rappers are required for each battle',
        type: 'warning',
      });
      return;
    }
    setVideoForms({
      ...videoForms,
      [youtubeId]: {
        ...videoForms[youtubeId],
        rappers: videoForms[youtubeId].rappers.filter((_, i) => i !== index),
      },
    });
  };

  // Client-side regex parser (fallback)
  const handleClientParse = (video) => {
    const parsed = parseBattleTitle(video.title);

    if (!parsed.success) {
      setAlertModal({
        show: true,
        title: 'Auto-Parse Failed',
        message: 'Could not auto-parse this video title. Please fill in the details manually.',
        type: 'warning',
      });
      return;
    }

    // Find matching artists
    const rapper1Match = findBestArtistMatch(parsed.rapper1, artists);
    const rapper2Match = findBestArtistMatch(parsed.rapper2, artists);

    // Build the form data
    const rappers = [
      {
        name: rapper1Match?.artist.display_name || rapper1Match?.artist.username || parsed.rapper1,
        existing_id: rapper1Match?.artist.id || null,
        confidence: rapper1Match?.confidence || 0,
        isExactMatch: rapper1Match?.isExactMatch || false,
      },
      {
        name: rapper2Match?.artist.display_name || rapper2Match?.artist.username || parsed.rapper2,
        existing_id: rapper2Match?.artist.id || null,
        confidence: rapper2Match?.confidence || 0,
        isExactMatch: rapper2Match?.isExactMatch || false,
      },
    ];

    // Select the video and set the form
    const newSelected = new Set(selectedVideos);
    newSelected.add(video.youtube_id);
    setSelectedVideos(newSelected);

    setVideoForms({
      ...videoForms,
      [video.youtube_id]: {
        rappers: rappers,
        event_name: parsed.eventName || '',
        parseConfidence: parsed.confidence,
      },
    });
  };

  // AI-powered LLM parser (primary)
  const handleAIParse = async (video) => {
    setParsing(true);
    try {
      const response = await youtubeAdminAPI.parseVideos([{
        youtube_id: video.youtube_id,
        title: video.title,
        description: video.description,
        tags: video.metadata?.tags || [],
      }]);

      const result = response.data.results[0];

      if (!result || (!result.rapper1 && !result.rapper2)) {
        // Fall back to client-side parser
        handleClientParse(video);
        return;
      }

      // Build rappers array from LLM result
      const rappers = [
        { name: result.rapper1 || '', existing_id: null },
        { name: result.rapper2 || '', existing_id: null },
      ];

      // Add any additional rappers from LLM
      if (result.additional_rappers && result.additional_rappers.length > 0) {
        result.additional_rappers.forEach(name => {
          rappers.push({ name, existing_id: null });
        });
      }

      // Try to match against existing artists
      rappers.forEach((rapper, index) => {
        if (rapper.name) {
          const match = findBestArtistMatch(rapper.name, artists);
          if (match) {
            rappers[index] = {
              name: match.artist.display_name || match.artist.username || rapper.name,
              existing_id: match.artist.id,
              confidence: match.confidence,
              isExactMatch: match.isExactMatch,
            };
          }
        }
      });

      // Select the video and populate form
      const newSelected = new Set(selectedVideos);
      newSelected.add(video.youtube_id);
      setSelectedVideos(newSelected);

      setVideoForms({
        ...videoForms,
        [video.youtube_id]: {
          rappers,
          event_name: result.event_name || '',
          round_count: result.round_count,
          format: result.format,
          parseConfidence: result.confidence,
          parseReasoning: result.reasoning,
          parsedByAI: true,
        },
      });
    } catch (error) {
      console.error('AI parse failed, falling back to client-side:', error);
      handleClientParse(video);
    } finally {
      setParsing(false);
    }
  };

  const PARSE_CHUNK_SIZE = 10;

  // Bulk AI parse for all unselected videos — chunked to avoid Render 30s timeout
  const handleAIParseAll = async () => {
    const unparsedVideos = videos.filter(v => !selectedVideos.has(v.youtube_id));

    if (unparsedVideos.length === 0) {
      setAlertModal({
        show: true,
        title: 'Nothing to Parse',
        message: 'All visible videos are already selected.',
        type: 'info',
      });
      return;
    }

    setParsing(true);

    // Split into chunks of PARSE_CHUNK_SIZE
    const chunks = [];
    for (let i = 0; i < unparsedVideos.length; i += PARSE_CHUNK_SIZE) {
      chunks.push(unparsedVideos.slice(i, i + PARSE_CHUNK_SIZE));
    }

    const totalBatches = chunks.length;
    setParseProgress({ current: 0, total: unparsedVideos.length, currentBatch: 0, totalBatches });

    const newSelected = new Set(selectedVideos);
    const newForms = { ...videoForms };
    let totalParsed = 0;
    let totalProcessed = 0;
    let failedChunks = 0;

    for (let chunkIdx = 0; chunkIdx < chunks.length; chunkIdx++) {
      const chunk = chunks[chunkIdx];
      setParseProgress({
        current: totalProcessed,
        total: unparsedVideos.length,
        currentBatch: chunkIdx + 1,
        totalBatches,
      });

      try {
        const videosToSend = chunk.map(v => ({
          youtube_id: v.youtube_id,
          title: v.title,
          description: v.description,
          tags: v.metadata?.tags || [],
        }));

        const response = await youtubeAdminAPI.parseVideos(videosToSend);
        const results = response.data.results;

        let chunkParsed = 0;
        results.forEach((result, index) => {
          if (result.rapper1 || result.rapper2) {
            const video = chunk[index];
            newSelected.add(video.youtube_id);

            const rappers = [
              { name: result.rapper1 || '', existing_id: null },
              { name: result.rapper2 || '', existing_id: null },
            ];

            if (result.additional_rappers?.length > 0) {
              result.additional_rappers.forEach(name => {
                rappers.push({ name, existing_id: null });
              });
            }

            // Match against existing artists
            rappers.forEach((rapper, i) => {
              if (rapper.name) {
                const match = findBestArtistMatch(rapper.name, artists);
                if (match) {
                  rappers[i] = {
                    name: match.artist.display_name || match.artist.username || rapper.name,
                    existing_id: match.artist.id,
                    confidence: match.confidence,
                    isExactMatch: match.isExactMatch,
                  };
                }
              }
            });

            newForms[video.youtube_id] = {
              rappers,
              event_name: result.event_name || '',
              round_count: result.round_count,
              format: result.format,
              parseConfidence: result.confidence,
              parseReasoning: result.reasoning,
              parsedByAI: true,
            };

            chunkParsed++;
          }
        });
        totalParsed += chunkParsed;
      } catch (error) {
        console.error(`Batch AI parse chunk ${chunkIdx + 1}/${totalBatches} failed:`, error);
        failedChunks++;
      }

      totalProcessed += chunk.length;

      // Update state progressively after each chunk
      setSelectedVideos(new Set(newSelected));
      setVideoForms({ ...newForms });
      setParseProgress({
        current: totalProcessed,
        total: unparsedVideos.length,
        currentBatch: chunkIdx + 1,
        totalBatches,
      });
    }

    setSelectedVideos(newSelected);
    setVideoForms(newForms);

    const failedMsg = failedChunks > 0 ? ` (${failedChunks} batch${failedChunks > 1 ? 'es' : ''} failed)` : '';
    setAlertModal({
      show: true,
      title: 'AI Parse Complete',
      message: `Parsed ${totalParsed} of ${unparsedVideos.length} videos${failedMsg}. Review the results and click Import when ready.`,
      type: failedChunks > 0 && totalParsed === 0 ? 'error' : 'success',
    });

    setParsing(false);
    setParseProgress({ current: 0, total: 0, currentBatch: 0, totalBatches: 0 });
  };

  const handleImport = async () => {
    const selectedVideosList = videos.filter((v) =>
      selectedVideos.has(v.youtube_id)
    );

    // Validate all selected videos have at least 2 rappers
    const invalid = selectedVideosList.filter((v) => {
      const form = videoForms[v.youtube_id];
      if (!form) return true;
      const filledRappers = form.rappers.filter((r) => r.name.trim() !== '');
      return filledRappers.length < 2;
    });

    if (invalid.length > 0) {
      setAlertModal({
        show: true,
        title: 'Incomplete Videos',
        message: `Please fill in at least 2 rapper names for all selected videos. ${invalid.length} video(s) incomplete.`,
        type: 'warning',
      });
      return;
    }

    setImporting(true);
    try {
      const importData = selectedVideosList.map((video) => ({
        youtube_id: video.youtube_id,
        title: video.title,
        description: video.description,
        thumbnail_url: video.thumbnail_url,
        published_at: video.published_at,
        view_count: video.view_count,
        duration: video.duration,
        metadata: video.metadata,
        rappers: videoForms[video.youtube_id].rappers.filter(
          (r) => r.name.trim() !== ''
        ),
        event_name: videoForms[video.youtube_id].event_name,
      }));

      const response = await youtubeAdminAPI.importVideos(importData);

      // Update artists list if response includes new artists
      if (response.data.artists) {
        setArtists(response.data.artists);
      }

      const hasErrors = response.data.error_count > 0;
      setAlertModal({
        show: true,
        title: hasErrors ? 'Import Complete with Errors' : 'Import Successful',
        message: `Imported: ${response.data.imported_count} videos\n${hasErrors ? `Errors: ${response.data.error_count}` : ''}`,
        type: hasErrors ? 'warning' : 'success',
      });

      if (response.data.errors && response.data.errors.length > 0) {
        console.error('Import errors:', response.data.errors);
      }

      // Clear selections and refresh
      setSelectedVideos(new Set());
      setVideoForms({});
      fetchVideos();
    } catch (error) {
      console.error('Import error:', error);
      setAlertModal({
        show: true,
        title: 'Import Failed',
        message: error.response?.data?.error || 'Failed to import videos',
        type: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleNextPage = () => {
    if (nextPageToken) {
      setPageTokenHistory([...pageTokenHistory, nextPageToken]);
      setCurrentPage(currentPage + 1);
      fetchVideos(nextPageToken);
    }
  };

  const handlePrevPage = () => {
    if (pageTokenHistory.length > 1) {
      const newHistory = [...pageTokenHistory];
      newHistory.pop(); // remove current
      const prevToken = newHistory[newHistory.length - 1];
      setPageTokenHistory(newHistory);
      setCurrentPage(currentPage - 1);
      fetchVideos(prevToken);
    } else if (currentPage > 1) {
      setPageTokenHistory([]);
      setCurrentPage(1);
      fetchVideos(null); // Go back to first page
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-battle-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-battle-dark py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            YouTube Battle Import
          </h1>
          <p className="text-gray-400">
            Import battles from the Verbal War Zone YouTube channel
          </p>
        </div>

        {/* Search Bar */}
        <SearchBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearch={handleSearch}
          onClear={() => {
            setSearchQuery('');
            setPageTokenHistory([]);
            fetchVideos(null, '');
          }}
        />

        {/* Import Actions */}
        <ImportSummary
          selectedCount={selectedVideos.size}
          importing={importing}
          onImport={handleImport}
          onAIParseAll={handleAIParseAll}
          parsing={parsing}
          parseProgress={parseProgress}
        />

        {/* Loading */}
        {loading ? (
          <div className="text-center py-12">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
              <div className="text-white text-xl">Loading videos from YouTube...</div>
              <div className="text-gray-500 text-sm">Fetching channel data and video details</div>
            </div>
          </div>
        ) : videos.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-xl">No videos found</div>
          </div>
        ) : (
          <>
            {/* Videos Table */}
            <VideoTable
              videos={videos}
              selectedVideos={selectedVideos}
              videoForms={videoForms}
              onSelectVideo={handleSelectVideo}
              onRapperChange={handleRapperChange}
              onRapperSelect={handleRapperSelect}
              onAddRapper={handleAddRapper}
              onRemoveRapper={handleRemoveRapper}
              onEventChange={handleEventChange}
              onAutoImport={handleAIParse}
              parsing={parsing}
            />

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              hasNext={!!nextPageToken}
              hasPrev={currentPage > 1}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
            />
          </>
        )}

        <AlertModal
          show={alertModal.show}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
        />
      </div>
    </div>
  );
};

export default YouTubeImport;
